import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';

interface SquareGuessGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

const SquareGuessGame: React.FC<SquareGuessGameProps> = ({ mode, onReset, language }) => {
  const [currentNumber, setCurrentNumber] = useState<number>(0);
  const [userAnswer, setUserAnswer] = useState<string>('');
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const generateQuestion = () => {
    const maxRoot = level === 1 ? 6 : level === 2 ? 8 : 10;
    const root = Math.floor(Math.random() * maxRoot) + 1;
    setCurrentNumber(root * root);
    setUserAnswer('');
    setShowFeedback(false);
  };

  useEffect(() => {
    generateQuestion();
  }, [level]);

  const handleSubmit = async () => {
    if (showFeedback) return;

    const userRoot = parseInt(userAnswer, 10);
    const correct = userRoot * userRoot === currentNumber;
    setIsCorrect(correct);
    setShowFeedback(true);
    setQuestionCount(questionCount + 1);

    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);

    if (correct) {
      setScore(score + 1);
    }

    setTimeout(() => {
      if (questionCount >= 4) {
        if (level < 3) {
          setLevel(level + 1);
          setQuestionCount(0);
        } else {
          setGameComplete(true);
          saveProgress(newStats);
        }
      } else {
        generateQuestion();
      }
    }, 2000);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'square',
        mode: 'guess',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        <div className="text-lg font-medium text-indigo-700 dark:text-indigo-300">
          {language === 'en' ? `Level ${level}` : `Niveau ${level}`}
        </div>
      </div>

      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Game Complete!' : 'Jeu terminé !'}
          </h2>
          <div className="mb-8">
            <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">
              {score}
            </div>
            <div className="text-slate-600 dark:text-slate-300">
              {language === 'en' ? 'Total Score' : 'Score total'}
            </div>
          </div>
          <button
            onClick={() => {
              setLevel(1);
              setScore(0);
              setGameComplete(false);
              setQuestionCount(0);
              generateQuestion();
              setStats({
                questionsAnswered: 0,
                correctAnswers: 0,
                incorrectAnswers: 0,
                totalTime: 0,
                averageResponseTime: 0,
              });
            }}
            className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
              hover:bg-indigo-700 dark:hover:bg-indigo-600 
              focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {language === 'en' 
                ? 'Find the number that was squared'
                : 'Trouvez le nombre qui a été élevé au carré'}
            </h2>
            <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
              {currentNumber}
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              {language === 'en'
                ? 'What number multiplied by itself gives this result?'
                : 'Quel nombre multiplié par lui-même donne ce résultat ?'}
            </p>
          </div>

          <div className="flex flex-col items-center gap-4">
            <input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              className={`w-32 p-4 text-2xl font-bold text-center rounded-lg border-2
                bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100
                focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400
                ${showFeedback
                  ? isCorrect
                    ? 'border-green-500 dark:border-green-500/50 bg-green-50 dark:bg-green-900/30'
                    : 'border-red-500 dark:border-red-500/50 bg-red-50 dark:bg-red-900/30'
                  : 'border-slate-200 dark:border-slate-600'
                }
              `}
              placeholder="?"
              disabled={showFeedback}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && userAnswer) {
                  handleSubmit();
                }
              }}
            />

            <button
              onClick={handleSubmit}
              disabled={!userAnswer || showFeedback}
              className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg 
                hover:bg-indigo-700 dark:hover:bg-indigo-600 
                focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {language === 'en' ? 'Check Answer' : 'Vérifier'}
            </button>

            {showFeedback && (
              <div className={`text-lg font-medium ${
                isCorrect 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {isCorrect
                  ? (language === 'en' ? 'Correct!' : 'Correct !')
                  : (language === 'en'
                    ? `The answer was ${Math.sqrt(currentNumber)}`
                    : `La réponse était ${Math.sqrt(currentNumber)}`)}
              </div>
            )}
          </div>

          <div className="mt-6 text-center text-slate-600 dark:text-slate-300">
            {language === 'en' ? 'Score: ' : 'Score : '}{score}
          </div>
        </div>
      )}
    </div>
  );
};

export default SquareGuessGame;